import {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import apiClient from '../../../helpers/apiClient'
import {handleApiError} from '../../../utils/handleApiError'
import {CatalogConfig, CatalogItem} from '../types'

type Props = {
  config: CatalogConfig
  onClose: () => void
  onSaved: () => void
}

const REORDER_STEP = 1

const SortableRow = ({id, label}: {id: number; label: string}) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id})
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className='nb-catalog-reorder__row'
    >
      <span className='nb-catalog-reorder__handle' aria-hidden='true'>
        ⋮⋮
      </span>
      <span>{label}</span>
    </li>
  )
}

export const CatalogReorderModal = ({config, onClose, onSaved}: Props) => {
  const intl = useIntl()
  const [items, setItems] = useState<CatalogItem[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  )

  useEffect(() => {
    let alive = true
    const fetchAll = async () => {
      try {
        setLoading(true)
        const rows: CatalogItem[] = []
        let offset = 0
        const pageSize = 200
        for (let guard = 0; guard < 50; guard++) {
          const {data} = await apiClient.get(config.endpoint, {
            params: {limit: pageSize, offset, ordering: 'order'},
          })
          const batch = data?.results ?? []
          rows.push(...batch)
          const total = typeof data?.count === 'number' ? data.count : rows.length
          offset += pageSize
          if (batch.length === 0 || rows.length >= total) break
        }
        if (alive) setItems(rows)
      } catch (err) {
        handleApiError(err, {})
      } finally {
        if (alive) setLoading(false)
      }
    }
    fetchAll()
    return () => {
      alive = false
    }
  }, [config.endpoint])

  const onDragEnd = (event: DragEndEvent) => {
    const {active, over} = event
    if (!over || active.id === over.id) return
    setItems((current) => {
      const oldIndex = current.findIndex((it) => it.id === active.id)
      const newIndex = current.findIndex((it) => it.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return current
      return arrayMove(current, oldIndex, newIndex)
    })
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const payload = items.map((item, index) => ({id: item.id, order: (index + 1) * REORDER_STEP}))
      // step=1: order is 1..N contiguous
      const {data} = await apiClient.post(`${config.endpoint}reorder/`, {items: payload})
      // touch data to satisfy strict lint rules; the response shape is {updated: n}
      void data
      onSaved()
      onClose()
    } catch (err) {
      handleApiError(err, {})
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='modal fade show' style={{display: 'block', zIndex: 1060}} tabIndex={-1} role='dialog'>
      <div className='modal-backdrop fade show' style={{zIndex: 1055}} onClick={onClose} />
      <div className='modal-dialog modal-dialog-centered' style={{position: 'relative', zIndex: 1061}}>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>
              {intl.formatMessage({id: 'CATALOG.REORDER.TITLE'})}
            </h5>
            <button type='button' className='btn-close' onClick={onClose} aria-label='Close' />
          </div>
          <div className='modal-body'>
            <p className='text-muted small'>
              {intl.formatMessage({id: 'CATALOG.REORDER.HELP'})}
            </p>
            {loading ? (
              <div className='text-center py-5'>
                {intl.formatMessage({id: 'CATALOG.LOADING'})}
              </div>
            ) : items.length === 0 ? (
              <div className='text-center py-5'>
                {intl.formatMessage({id: 'CATALOG.EMPTY'})}
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext
                  items={items.map((it) => it.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className='nb-catalog-reorder__list'>
                    {items.map((item) => (
                      <SortableRow
                        key={item.id}
                        id={item.id}
                        label={String(item[config.primaryField] ?? '')}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-outline btn-outline-dark' onClick={onClose}>
              {intl.formatMessage({id: 'CATALOG.MODAL.CANCEL'})}
            </button>
            <button
              type='button'
              className='tap-add-experience-2-btn-secondary'
              onClick={handleSave}
              disabled={saving || loading || items.length === 0}
            >
              {intl.formatMessage({id: saving ? 'CATALOG.BUTTON.SAVING' : 'CATALOG.BUTTON.SAVE'})}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
